import React from 'react';
import { CENTRA_EVENT_ROUTES, CENTRA_ROUTE_SCHEMA, EVENT_EMITTER, EVENTS_NAME } from '@src/consts/common';
import { GetRequest, IPredicate, MutateRequest } from '@src/models/api';
import { AnyType } from '@src/models/common';
import { TFeedbackFormType } from './models';
import { ILegoElem } from '../Lego';
import {
  BASIC_BIO_ELEM,
  COMPANY_TITLE,
  EMAIL_INPUT,
  MAILING_AGREEMENT_CHECKBOX,
  NAME_INPUT,
  PESONAL_INFO_CHECKBOX,
  PHONE_NUMBER_INPUT,
  TARIFF_ICONS,
  USER_AGREEMENT_CHECKBOX,
} from './const';
import { Box } from '@src/atoms/Box';
import { Icon } from '@src/atoms/Icon';
import { Text } from '@src/atoms/Text';
import { toUpperFirstSymbol } from '@src/utils/string';
import { ERROR_MESSAGES } from '@src/consts/errors';
import { ILegoElemTag } from '../Lego/_Elem/models';
import { DEMO_FUNCTION_TOOLTIP } from '@src/consts/demo';

export const getTime = (v: string, check: boolean) => {
  return check ? v : '';
};

export const downgradeFirstSymbol = (v: string) => {
  if (typeof v !== 'string') {
    return v;
  }

  return v
    .split('')
    .map((s, i) => (i === 0 ? s.toLocaleLowerCase() : s))
    .join('');
};

/** @deprecated */
export const openFeedbackForm = (type: TFeedbackFormType, data?: { [x: string]: string }) => {
  if (typeof window === 'undefined') return;

  const query = data
    ? Object.entries(data)
        .map(([key, value]) => (key && value ? `${key}=${value}` : ''))
        .join('&')
    : '';

  const link = `${CENTRA_ROUTE_SCHEMA}${CENTRA_EVENT_ROUTES.FEEDBACK}?formId=${type}${query ? `&${query}` : ''}`;
  EVENT_EMITTER.emit(EVENTS_NAME.CENTRA_ROUTE_EVENT, { link });
};

export const prepareDialogData = (kind: TFeedbackFormType, formData: { [x: string]: AnyType }): MutateRequest => {
  const trimmedFormData = {};

  for (const field in formData)
    trimmedFormData[field] = typeof formData[field] === 'string' ? formData[field].trim() : formData[field];

  return {
    operations: [
      {
        operator: 'ADD',
        operand: {
          kind,
          formData: trimmedFormData,
        },
      },
    ],
  };
};

/** подготовка данных для запроса продукта */
export const getProductRequest = (productId: string | number): GetRequest => {
  const predicates: IPredicate[] = [
    { field: 'productId', operator: 'EQUALS', values: [productId] },
    { field: 'productStatus', operator: 'IN', values: ['published'] },
  ];

  return {
    selector: {
      fields: ['productId', 'productName'],
      predicates,
      ordering: [{ field: 'productTypeLinkOrderForPublicStore', sortOrder: 'ASC' }],
      paging: { limit: 100 },
    },
  };
};

export const getProductBundleRequest = (bundleId: string | number): GetRequest => {
  const predicates: IPredicate[] = [{ field: 'id', operator: 'EQUALS', values: [bundleId] }];

  return {
    selector: {
      fields: ['id', 'name'],
      predicates,
      paging: { limit: 100 },
    },
  };
};

export const buildLegoElems = (
  type,
  product,
  productBundle,
  tariff,
  services,
  isAuthZone,
  loading,
  isMobile,
  isDemoUser,
) => {
  switch (type) {
    case 'anyQuestion':
      return buildAnyQuestionElems(isAuthZone, loading, isMobile, isDemoUser);
    case 'requestConditions':
      return buildRequestConditionsElems(product, isAuthZone, loading, isMobile, isDemoUser);
    case 'requestProductConnect':
      return buildRequestProductConnectElems(product, productBundle, isAuthZone, loading, isMobile, isDemoUser);
    case 'requestDemo':
      return buildRequestDemoElems(isAuthZone, loading, isMobile, false);
    case 'requestPartner':
      return buildRequestPartnersElems(isAuthZone, loading, isMobile, isDemoUser);
    case 'requestProduct':
      return buildRequestProductElems(isAuthZone, loading, isMobile, isDemoUser);
    case 'requestTariff':
      return buildRequestTariffElems(tariff, services, isAuthZone, loading, isMobile, isDemoUser);
    case 'requestEdu':
      return buildRequestEduElems(isAuthZone, loading, isMobile, isDemoUser);
    case 'requestProductFilter':
      return buildRequestProductFilterElems(loading, isMobile, isDemoUser);
  }
};

const buildAnyQuestionElems = (isAuthZone, loading, isMobile, isDemoUser): ILegoElem[] => {
  const checkBoxes = isAuthZone ? [PESONAL_INFO_CHECKBOX] : [PESONAL_INFO_CHECKBOX, MAILING_AGREEMENT_CHECKBOX];
  return [
    {
      id: 'title',
      text: {
        id: 'title',
        size: 'h3',
      },
      content: 'Есть вопросы?',
    },
    {
      id: 'subTitle',
      text: {
        id: 'subTitle',
        withMargin: true,
      },
      content: 'Отправьте нам заявку, и мы свяжемся с вами в удобной для вас форме',
    },
    ...BASIC_BIO_ELEM,
    {
      id: 'question',
      textarea: {
        label: 'Ваше сообщение',
        size: 'md',
      },
      validators: {
        dataType: 'STRING',
      },
    },
    ...checkBoxes,
    BUTTONS_LEGO(loading, isMobile, isDemoUser),
  ];
};

const buildRequestConditionsElems = (product, isAuthZone, loading, isMobile, isDemoUser): ILegoElem[] => {
  const checkBoxes = isAuthZone ? [PESONAL_INFO_CHECKBOX] : [PESONAL_INFO_CHECKBOX, MAILING_AGREEMENT_CHECKBOX];
  const title = product ? `Заявка на ${product.productName}` : '';
  return [
    {
      id: 'title',
      text: {
        id: 'title',
        size: 'h3',
      },
      content: title,
    },
    {
      id: 'subTitle',
      text: {
        id: 'subTitle',
        withMargin: true,
      },
      content:
        'Заполните заявку и менеджер Centra свяжется с вами, чтобы сформировать индивидуальное предложение и подключить продукт',
    },
    ...BASIC_BIO_ELEM,
    COMPANY_TITLE,
    {
      id: 'comment',
      textarea: {
        label: 'Комментарий',
        size: 'md',
      },
      validators: {
        dataType: 'STRING',
      },
    },
    ...checkBoxes,
    BUTTONS_LEGO(loading, isMobile, isDemoUser),
  ];
};

const buildRequestProductConnectElems = (
  product,
  productBundle,
  isAuthZone,
  loading,
  isMobile,
  isDemoUser,
): ILegoElem[] => {
  const checkBoxes = isAuthZone ? [PESONAL_INFO_CHECKBOX] : [PESONAL_INFO_CHECKBOX, MAILING_AGREEMENT_CHECKBOX];
  const title = product ? `Заявка на ${product.productName}` : '';
  const titleBundle = productBundle ? `Заявка на ${productBundle.name}` : '';
  return [
    {
      id: 'title',
      text: {
        id: 'title',
        size: 'h3',
      },
      content: title || titleBundle,
    },
    {
      id: 'subTitle',
      text: {
        id: 'subTitle',
        withMargin: true,
      },
      content:
        'Заполните заявку и менеджер Centra свяжется с вами, чтобы сформировать индивидуальное предложение и подключить продукт',
    },
    NAME_INPUT,
    COMPANY_TITLE,
    PHONE_NUMBER_INPUT,
    EMAIL_INPUT,
    {
      id: 'comment',
      textarea: {
        label: 'Комментарий',
        size: 'md',
      },
      validators: {
        dataType: 'STRING',
      },
    },
    ...checkBoxes,
    BUTTONS_LEGO(loading, isMobile, isDemoUser),
  ];
};

const buildRequestDemoElems = (isAuthZone, loading, isMobile, isDemoUser): ILegoElem[] => {
  const checkBoxes = isAuthZone ? [PESONAL_INFO_CHECKBOX] : [PESONAL_INFO_CHECKBOX, MAILING_AGREEMENT_CHECKBOX];
  return [
    {
      id: 'title',
      text: {
        id: 'title',
        size: 'h3',
      },
      content: 'Заявка на подключение',
    },
    {
      id: 'subTitle',
      text: {
        id: 'subTitle',
        withMargin: true,
      },
      content: 'Заполните заявку, и менеджер Centra свяжется с вами для подключения платформы',
    },
    ...BASIC_BIO_ELEM,
    ...checkBoxes,
    BUTTONS_LEGO(loading, isMobile, isDemoUser),
  ];
};

const buildRequestPartnersElems = (isAuthZone, loading, isMobile, isDemoUser): ILegoElem[] => {
  const checkBoxes = isAuthZone ? [PESONAL_INFO_CHECKBOX] : [PESONAL_INFO_CHECKBOX, MAILING_AGREEMENT_CHECKBOX];
  return [
    {
      id: 'title',
      text: {
        id: 'title',
        size: 'h3',
      },
      content: 'Работайте с Centra',
    },
    {
      id: 'subTitle',
      text: {
        id: 'subTitle',
        withMargin: true,
      },
      content:
        'Хотите стать партнером Centra? Заполните заявку на размещение продукта в маркетплейсе Centra. Мы свяжемся с вами и расскажем, как стать нашим партнером и опции сотрудничества.',
    },
    NAME_INPUT,
    COMPANY_TITLE,
    PHONE_NUMBER_INPUT,
    EMAIL_INPUT,
    {
      id: 'telegram',
      input: {
        label: 'Никнейм в телеграм',
        number: true,
        size: 'md',
      },
      validators: {
        dataType: 'STRING',
        custom: [
          {
            name: 'telegramNickname',
            test: (value: AnyType) => {
              if (!value || value.match(/^\s*@(?!$).+/g)) return true;
              return false;
            },
            message: ERROR_MESSAGES.TELEGRAM,
          },
        ],
      },
    },
    ...checkBoxes,
    BUTTONS_LEGO(loading, isMobile, isDemoUser),
  ];
};

const buildRequestProductElems = (isAuthZone, loading, isMobile, isDemoUser): ILegoElem[] => {
  const checkBoxes = isAuthZone ? [PESONAL_INFO_CHECKBOX] : [PESONAL_INFO_CHECKBOX, MAILING_AGREEMENT_CHECKBOX];
  return [
    {
      id: 'title',
      text: {
        id: 'title',
        size: 'h3',
      },
      content: 'Какой продукт вам нужен?',
    },
    {
      id: 'subTitle',
      text: {
        id: 'subTitle',
        withMargin: true,
      },
      content: 'Напишите нам, и мы подключим его для вас в короткие сроки',
    },
    ...BASIC_BIO_ELEM,
    {
      id: 'product',
      textarea: {
        label: 'Продукт',
        size: 'md',
      },
      validators: {
        dataType: 'STRING',
      },
    },
    ...checkBoxes,
    BUTTONS_LEGO(loading, isMobile, isDemoUser),
  ];
};

const buildRequestTariffElems = (
  tariff,
  services: string | undefined,
  isAuthZone,
  loading,
  isMobile,
  isDemoUser,
): ILegoElem[] => {
  const checkBoxes = isAuthZone ? [PESONAL_INFO_CHECKBOX] : [PESONAL_INFO_CHECKBOX, MAILING_AGREEMENT_CHECKBOX];
  const arr = services?.split(',').map((el, idx) => {
    return {
      id: `tag${idx}`,
      tag: {
        id: `tag${idx}`,
        view: 'basicGreySemiLight',
      },
      content: (
        <Box gap={4} alignItems={'center'}>
          <Icon id="tagIcon" name="check" size="sm" view="basicGreyDark" />
          <Text id="tagText" size="caption" tag="span" style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {el}
          </Text>
        </Box>
      ),
    };
  });

  const iconName = TARIFF_ICONS[tariff || ''];
  const color = iconName === 'rocketSmallPro' ? 'basicWhite' : 'basicBlack';
  const view = iconName === 'rocketSmallPro' ? 'basicBlack' : 'basicWhite';
  return [
    {
      id: 'title',
      lego: {
        boxWrap: {
          gap: 8,
          alignItems: 'center',
        },
        elems: [
          {
            id: 'title',
            text: {
              id: 'title',
              size: 'h3',
            },
            content: `Подключить тариф`,
          },
          {
            id: 'tariffTag',
            tag: {
              id: 'tariffTag',
              view: view,
              style: { padding: '2px 8px 2px 4px', borderRadius: '8px', height: '28px' },
            },
            content: (
              <Box>
                <Icon name={iconName} view={color} />
                <Text id="tag" weight="bold" color={color} size="h4">
                  {toUpperFirstSymbol(tariff)}
                </Text>
              </Box>
            ),
          },
        ],
      },
    },
    {
      id: 'tags',
      lego: {
        boxWrap: {
          gap: 8,
        },
        elems: arr as ILegoElemTag[],
      },
    },
    {
      id: 'subTitle',
      text: {
        id: 'subTitle',
        withMargin: true,
      },
      content: 'Оставьте заявку, и менеджер Centra свяжется с вами в удобное время для согласования условий',
    },
    ...BASIC_BIO_ELEM,
    ...checkBoxes,
    BUTTONS_LEGO(loading, isMobile, isDemoUser),
  ];
};

const buildRequestEduElems = (isAuthZone, loading, isMobile, isDemoUser): ILegoElem[] => {
  const checkBoxes = isAuthZone
    ? [PESONAL_INFO_CHECKBOX]
    : [PESONAL_INFO_CHECKBOX, USER_AGREEMENT_CHECKBOX, MAILING_AGREEMENT_CHECKBOX];
  return [
    {
      id: 'title',
      text: {
        id: 'title',
        size: 'h3',
      },
      content: 'Заявка на обучение',
    },
    {
      id: 'subTitle',
      text: {
        id: 'subTitle',
        withMargin: true,
      },
      content:
        'Оставьте заявку, и менеджер Centra свяжется с вами в удобное время для консультации и согласования условий',
    },
    ...BASIC_BIO_ELEM,
    ...checkBoxes,
    BUTTONS_LEGO(loading, isMobile, isDemoUser),
  ];
};

const buildRequestProductFilterElems = (loading, isMobile, isDemoUser): ILegoElem[] => {
  const titleContent = isMobile ? (
    <>
      <Text id="firstPartMobileTitle" size="h3">
        Каких критериев вам
      </Text>
      <Text id="secondPartMobileTitle" size="h3">
        не хватает для поиска нужных продуктов?
      </Text>
    </>
  ) : (
    'Каких критериев вам не хватает для поиска нужных продуктов?'
  );
  return [
    {
      id: 'title',
      text: {
        id: 'title',
        size: 'h3',
      },
      content: titleContent,
    },
    {
      id: 'subTitle',
      text: {
        id: 'subTitle',
        withMargin: true,
      },
      content: 'Напишите нам, и мы добавим их в Витрину',
    },
    {
      id: 'filter',
      textarea: {
        size: 'md',
      },
      validators: {
        dataType: 'STRING',
        required: true,
      },
    },
    BUTTONS_LEGO(loading, isMobile, isDemoUser),
  ];
};

const BUTTONS_LEGO = (loading, isMobile, isDemoUser): ILegoElem => {
  return {
    boxWrap: {
      style: { width: isMobile ? '100%' : '178px' },
    },
    id: 'submitBtn',
    button: {
      id: 'submitBtn',
      size: 'lg',
      view: 'primaryLight',
      type: 'submit',
      disabled: loading || isDemoUser,
      tooltipContent: isDemoUser ? DEMO_FUNCTION_TOOLTIP : undefined,
      fullWidth: false,
    },
    content: 'Отправить',
  };
};
